import React, { useEffect, useState, Suspense, lazy } from "react";

import {
	Route,
	Routes,
	useLocation
} from "react-router-dom";

import { pharmacyMembershipAccessList } from "../auth/permissions";
import { AnnouncementBar, Header, ImportantChangeLogPopup, RouteLoadingFallback } from "../components/common";
import { NotFound } from "../components/error/notFound";
import { useCustomSelectors } from "../services/selectors/allSelectors";

import { authToken } from "../storage/authToken";
import { removeWorkSpaceTab } from "../utils/helper/helper";
import { ToastContainer, Flip } from 'react-toastify';
import { ToastErrorBoundary } from "../components/common/ToastErrorBoundary";
// import { usePackagingSelector } from "../services/selectors/pacakingSelector";

// import SignIn from "../auth/signIn";
import SignIn from "../auth/twoFactorSignIn";
import ResponsiveChatView from "../components/chat/responsiveChatView";
import WorkspaceTabs from "../components/pharmacy/workSpace/workspaceTabs";
import ProtectedRoute from "./protectedRoutes";

import useMyModules, { buildModuleGate } from "../hooks/useMyModules";
import usePharmacyFeatureFlags from "../hooks/usePharmacyFeatureFlags";
import { ModuleGuard } from "./moduleGuard";
import * as pages from "../screens";
import * as adminpage from "../screens/admin";
import * as routeNames from "./routeName";
import ChatBox from "../components/chat/chatbox";
import DailyRoutinePanel from "../screens/dashboard/notifications/components/DailyRoutinePanel";

// Lazy load components for code splitting (same pattern across app)
const BookkeeperMobileLogin = lazy(() => import("../screens/pharmacy/payroll/bookkeeperMobileLogin"));
const Pharmacare = lazy(() => import("../screens/pharmacy/forms/pharmacare"));
const InventoryApp = lazy(() => import("../inventory/InventoryApp"));

const PharmacistSignatureRoute = () => {
	const location = useLocation()
	const hasAuthorizedToken = new URLSearchParams(location.search).has('authorzied')
		|| new URLSearchParams(location.search).has('authorized')

	if (hasAuthorizedToken) {
		return <pages.AddNewPharmacistSignatures />
	}

	return <ProtectedRoute Component={pages.AddNewPharmacistSignatures}></ProtectedRoute>
}

const AppRoutes = () => {

	const token = authToken();
	const location = useLocation()
	const [isSticky, setIsSticky] = useState(false);

	const { pharmacyLoginUser,
		pharmacyLoginData,
		adminModule,
		rawAdminModule,
		pharmacyId,
		profileData,
		accountabilitySettings,
		// showAfterProcessPrepCheck,
		bookkeeperModuleAccess } = useCustomSelectors()

	// const { fetchDashboardCountsResponse } = usePackagingSelector()

	const isPermissionAccess = pharmacyMembershipAccessList()
	const featureFlags = usePharmacyFeatureFlags(pharmacyLoginData?.app_mode)

	// New module-permission system. FAIL-OPEN: myModules is null whenever the
	// backend cannot answer (old backend 404, 500, network) and both checks then
	// always pass, so every route group keeps today's exact legacy behaviour.
	// When non-null they only RESTRICT, on top of the untouched legacy
	// conditions below.
	//
	// moduleAllows(key) is the top-level (menu) check; `guard` adds the
	// sub-module check for the routes that have one. `modules` carries both the
	// leaf keys and the derived parent keys, so moduleAllows keeps working
	// unchanged for parents.
	const myModules = useMyModules()
	const { legacy: isLegacyModuleResponse, hasKey } = buildModuleGate(myModules)
	const moduleAllows = (moduleKey) => hasKey(moduleKey)
	const guard = (moduleKey) => <ModuleGuard
		allowed={moduleAllows(moduleKey)}
		hasKey={myModules.modules === null ? null : hasKey}
		loading={myModules.loading}
	/>
	// New module assignments are authoritative once /my-modules replies.  The
	// older per-user flags remain the complete fallback for an old backend or a
	// failed module request.  Route definitions must follow that same rule as
	// the workspace tabs; otherwise a visible new-system tab can point at a
	// route which was never registered and render NotFound.
	const shouldRegisterModuleRoutes = (legacyAllows) => (
		myModules.loading || !isLegacyModuleResponse || legacyAllows
	)
	const isKnownRoute = Object.values(routeNames).includes(location.pathname);
	const isPharmacistSignatureRoute = location.pathname === routeNames.pharmacy_pharmacist_signature_updation;
	const isPharmacistUser = profileData?.type === 'pharmacist';

	useEffect(() => {
		const handleScroll = () => {
			if (window.scrollY >= 50) {
				setIsSticky(true);
			} else {
				setIsSticky(false);
			}
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		if (location.pathname === routeNames.HOME_ROUTE) {
			removeWorkSpaceTab()
		}
	}, [])

	return (
		<>
			<ToastErrorBoundary>
				<ToastContainer
					position="top-right"
					autoClose={3000}
					hideProgressBar={true}
					newestOnTop={true}
					closeOnClick
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme="light"
					limit={3}
					stacked={true}
					style={{
						zIndex: 9999
					}}
					toastStyle={{
						background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
						border: '1px solid #e5e5e5',
						borderRadius: '12px',
						boxShadow: 'transparent',
						padding: '8px 20px',
						minHeight: '64px',
						fontSize: '16px',
						lineHeight: '1.2',
						transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
					}}
					transition={Flip}
					closeButton={false}
					className="custom-toast-container"
				/>
			</ToastErrorBoundary>
			<ToastErrorBoundary>
				<ToastContainer
					containerId="med-review"
					position="bottom-left"
					className="toast-container-med-review"
					autoClose={false}
					hideProgressBar={true}
					closeOnClick={false}
					draggable
					pauseOnHover
					theme="light"
					limit={1}
					style={{ zIndex: 9999 }}
					toastStyle={{ background: 'transparent', boxShadow: 'none', padding: 0 }}
					transition={Flip}
					closeButton={false}
				/>
			</ToastErrorBoundary>



			{(location.pathname !== routeNames.pharmacy_pharmacist_signature_updation
				&& location.pathname !== routeNames.pharmacy_accountability_upload_pics
				&& location.pathname !== routeNames.pharmacy_patient_med_review_sign
				&& location.pathname !== routeNames.pharmacy_payroll_signature
				&& location.pathname !== routeNames.pharmacy_pharmanet_profile
				&& location.pathname !== routeNames.bookkeeper_mobile_login)
				&& token
				&& isKnownRoute && (
					<>
						<AnnouncementBar />
						<ImportantChangeLogPopup />
						{(location.pathname === routeNames.HOME_ROUTE || !routeNames.HOME_ROUTE.includes(location.pathname)) && (
							<div className="header bg-[#3A52BB]">
								<Header />
							</div>
						)}
						{(location.pathname === routeNames.HOME_ROUTE || !routeNames.HOME_ROUTE.includes(location.pathname)) && (
							<div className={`${isSticky ? 'sticky top-0 z-30' : ''}`}>
								<WorkspaceTabs />
							</div>
						)}
						<ChatBox />
					</>
				)}
			{token && isKnownRoute && location.pathname !== routeNames.DASHBOARD_ROUTE && !isPharmacistSignatureRoute && location.pathname !== routeNames.pharmacy_pharmanet_profile && (
				<DailyRoutinePanel />
			)}
			<Routes>

				{!token && <Route exact path={routeNames.HOME_ROUTE} element={<SignIn />} />}
				<Route
					path="/inventory/*"
					element={
						<Suspense fallback={<RouteLoadingFallback />}>
							<InventoryApp />
						</Suspense>
					}
				/>

				{/* Public Bookkeeper Mobile Login Route - No authentication required, mobile only */}
				<Route
					exact
					path={routeNames.bookkeeper_mobile_login}
					element={
						<Suspense fallback={<RouteLoadingFallback />}>
							<BookkeeperMobileLogin />
						</Suspense>
					}
				/>

				{/* Unprotected Patient Routes */}
				<Route exact path={`${routeNames.pharmacy_patient_med_review_sign}`} element={<pages.PatientMedReviewSign />} />
				<Route exact path={`${routeNames.pharmacy_payroll_signature}`} element={<pages.PayrollSignature />} />

				{/* Common Routes */}
				<Route exact path={routeNames.DASHBOARD_ROUTE} element={<ProtectedRoute Component={pages.Dashboard}></ProtectedRoute>} />
				<Route exact path={routeNames.pharmacy_bookmarks} element={<ProtectedRoute Component={pages.PharmacyBookMarksList}></ProtectedRoute>} />
				<Route exact path={routeNames.TIMESHEET_ROUTE} element={<ProtectedRoute Component={pages.TimeSchedule}></ProtectedRoute>} />
				<Route exact path={routeNames.APPRECIATIONS_ROUTE} element={<ProtectedRoute Component={pages.Appreciation}></ProtectedRoute>} />
				<Route exact path={routeNames.NEWREQUEST_ROUTE} element={<ProtectedRoute Component={pages.NewRequest}></ProtectedRoute>} />
				{/* INVERTED CHECK — this route exists for NON-admins. It reads the
				    user's REAL admin flag so that screen privacy (F9 x5) cannot
				    quietly register a route that was not there before: hiding may
				    only ever remove. */}
				{rawAdminModule !== 1 && <Route exact path={routeNames.ALLREQUEST_ROUTE} element={<ProtectedRoute Component={pages.AllRequests}></ProtectedRoute>} />}
				<Route exact path={routeNames.SETTINGS_ROUTE} element={<ProtectedRoute Component={pages.Settings}></ProtectedRoute>} />
				<Route exact path={routeNames.CHANGE_LOG_ROUTE} element={<ProtectedRoute Component={pages.ChangeLog}></ProtectedRoute>} />
				<Route exact path={routeNames.REALTIME_DEMO_ROUTE} element={<ProtectedRoute Component={pages.RealtimeDemoPage}></ProtectedRoute>} />
				{/* <Route exact path={routeNames.ALLEMPLOYEES_ROUTE} element={<ProtectedRoute Component={pages.AllEmployees}></ProtectedRoute>} /> */}
				<Route exact path={`${routeNames.EDIT_TIME_OFFR_REQUEST_ROUTE}/:id`} element={<ProtectedRoute Component={pages.EditRequest}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_break_parroll_settings}`} element={<ProtectedRoute Component={pages.BreakPayrollSetting}></ProtectedRoute>} />
				{/* {adminModule === 1 && (
					Number(pharmacyId) === 1502 || 
					(bookkeeperModuleAccess?.data?.has_access === true)
				) && <Route exact path={`${routeNames.pharmacy_admin_payroll}`} element={<ProtectedRoute Component={pages.AdminPayroll}></ProtectedRoute>} />} */}
				{/* "Financials" in the ACCOUNTABILITY dropdown — mapped to the
				    `accountability_financials` sub-module, hidden from the nav
				    without it, but the route itself was unguarded and reachable
				    by URL. The adminModule / 1502 / bookkeeper conditions are
				    kept verbatim; the guard only ANDs on top. */}
				<Route element={guard('accountability')}>
				{adminModule === 1 && (
					Number(pharmacyId) === 1502 ||
					(bookkeeperModuleAccess?.data?.has_access === true || bookkeeperModuleAccess?.data?.hasAccountingAccess === true)
				) && <Route exact path={`${routeNames.pharmacy_accounts}`} element={<ProtectedRoute Component={pages.Accounts}></ProtectedRoute>} />}
				</Route>

				<Route exact path={`${routeNames.CHAT_NOW}/:user_id`} element={<ProtectedRoute Component={ResponsiveChatView}></ProtectedRoute>} />
				<Route exact path={`${routeNames.MY_PROFILE}`} element={<ProtectedRoute Component={pages.MyProfile}></ProtectedRoute>} />
				

				{/* Pharmacy Routes */}
				<Route exact path={`${routeNames.pharmacy_search_module}`} element={<ProtectedRoute Component={pages.GeneralInformation}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_all_notifications}`} element={<ProtectedRoute Component={pages.AllNotifications}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_front_profile}`} element={<ProtectedRoute Component={pages.PharmacyFrontProfile}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_fax_log}`} element={<ProtectedRoute Component={pages.SentFaxLogs}></ProtectedRoute>} />
				{/* Pharmacy Routes */}

				{/* Patient Routes */}
				<Route element={guard('patients')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_patient === pharmacyLoginUser?.pharmacy_patient) && <>
					<Route exact path={routeNames.ADD_PATIENTS} element={<ProtectedRoute Component={pages.AddPatients}></ProtectedRoute>} />
					<Route exact path={`${routeNames.ALL_PATIENTS}`} element={<ProtectedRoute Component={pages.AllPatientsList}></ProtectedRoute>} />
					<Route exact path={`${routeNames.PHARMACY_PATIENT_PROFILE}`} element={<ProtectedRoute Component={pages.PharmacyPatientProfile}></ProtectedRoute>} />
				</>}
				</Route>
				{/* Patient Routes */}

				{/* Forms Routes */}
				<Route element={guard('forms')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_forms === pharmacyLoginUser?.pharmacy_forms) && <>
					<Route exact path={`${routeNames.PHARMACY_INFORMATION_UPDATE_FORM}`} element={<ProtectedRoute Component={pages.PharmacyInformationUpdateForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.PHARMACY_PRESCRIPTION_TRASNSFER_FORM}`} element={<ProtectedRoute Component={pages.RxTransferForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_Emergency_Supply_OAT_medications_form}`} element={<ProtectedRoute Component={pages.DrNotificationForEmergencySupply}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_medication_form}`} element={<ProtectedRoute Component={pages.AntiViralRequestForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.missing_Rx_Requirements}`} element={<ProtectedRoute Component={pages.MixingRxRequirementsForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_emergency_supply_continuity_form}`} element={<ProtectedRoute Component={pages.EmergencySupplyForContinuityOfCareForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.patient_agreement_for_take_home_dosing}`} element={<ProtectedRoute Component={pages.PatientAgreementForTakeHomeDosing}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_Plan_G_Form}`} element={<ProtectedRoute Component={pages.PlanGForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_Plan_Z_Form}`} element={<ProtectedRoute Component={pages.PlanZForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_doctor_communication_form}`} element={<ProtectedRoute Component={pages.DoctorCommunicationForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_Pharmacist_Prescriber_Communication}`} element={<ProtectedRoute Component={pages.PharmacistPrescriberCommunication}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_original_prescription_form}`} element={<ProtectedRoute Component={pages.RxCopyOfOriginalForOatDD}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_doctor_note_form}`} element={<ProtectedRoute Component={pages.AddDoctorNote}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_tb_skin_test_mantoux_form}`} element={<ProtectedRoute Component={pages.TBSkinMantouxForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_tb_skin_test_logs}`} element={<ProtectedRoute Component={pages.TbSkinTestLogs}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_tb_form}`} element={<ProtectedRoute Component={pages.TBScreeningForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_tb_screening_form_logs}`} element={<ProtectedRoute Component={pages.TbScreeningFormsLogs}></ProtectedRoute>} />
				</>}
				</Route>
				{/* Forms Routes */}

				{/* Compounds Routes */}
				<Route element={guard('compound')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_compounds === pharmacyLoginUser?.pharmacy_compounds) && <>
					<Route exact path={`${routeNames.pharmacy_compounds}`} element={<ProtectedRoute Component={pages.CompoundPriceList}></ProtectedRoute>} />
					{isPermissionAccess?.pharmacy_compound_price_list === pharmacyLoginUser?.pharmacy_compound_price_list && <>
						<Route exact path={`${routeNames.pharmacy_compound_price_list}`} element={<ProtectedRoute Component={pages.EditCompoundPriceList}></ProtectedRoute>} />
					</>
					}
					<Route exact path={`${routeNames.pharmacy_compound_agreement_form}`} element={<ProtectedRoute Component={pages.CompoundAggrementForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_controlled_order_request}`} element={<ProtectedRoute Component={pages.ControlledSubstancesOrderRequestForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_order_compound_form}`} element={<ProtectedRoute Component={pages.CompoundOrderForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_order_compounding_form}`} element={<ProtectedRoute Component={pages.OrderCompoundingForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_compound_quotations}`} element={<ProtectedRoute Component={pages.CompoundQuotation}></ProtectedRoute>} />
					{/* <Route exact path={`${routeNames.pharmacy_compound_dins}`} element={<ProtectedRoute Component={pages.CompoundDins}></ProtectedRoute>} /> */}
					{/* <Route exact path={`${routeNames.pharmacy_compound_logs}`} element={<ProtectedRoute Component={pages.CompoundLogs}></ProtectedRoute>} /> */}
					<Route exact path={`${routeNames.pharmacy_compound_due_billing}`} element={<ProtectedRoute Component={pages.CompoundDueBilling}></ProtectedRoute>} />
				</>}
				</Route>
				{/* Compounds Routes */}

				{/* Dispense Routes */}
				<Route element={guard('dispense')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_daily_dispense === pharmacyLoginUser?.pharmacy_daily_dispense) && <>
					<Route exact path={`${routeNames.pharmacy_archived_deliveries}`} element={<ProtectedRoute Component={pages.ArchivedDispense}></ProtectedRoute>} />
					{/* {isPharmacistUser && <Route exact path={`${routeNames.pharmacy_driver_clinic_call_logs}`} element={<ProtectedRoute Component={pages.DriverClinicCallLogs}></ProtectedRoute>} />} */}
					<Route exact path={`${routeNames.pharmacy_delivery_lables}`} element={<ProtectedRoute Component={pages.DeliveryLabels}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_expiring_rx}`} element={<ProtectedRoute Component={pages.ExpiringRx}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_expiring_rx_v2}`} element={<ProtectedRoute Component={pages.ExpiringRxV2}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_patient_counselling}`} element={<ProtectedRoute Component={pages.Counselling}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_consent_form_logs}`} element={<ProtectedRoute Component={pages.ConsentSubmissions}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_memo}`} element={<ProtectedRoute Component={pages.PharmacyMemo}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_drugs_billing}`} element={<ProtectedRoute Component={pages.BillingList}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_oat_prescriptions}`} element={<ProtectedRoute Component={pages.PrescriptionsList}></ProtectedRoute>} />
					{/* <Route exact path={`${routeNames.upload_prescription}`} element={<ProtectedRoute Component={pages.uploadePrescription}></ProtectedRoute>} /> */}
					<Route exact path={`${routeNames.pharmacy_ongoing_prescritiptions}`} element={<ProtectedRoute Component={pages.OnGoingPrescriptions}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_returned_patches}`} element={<ProtectedRoute Component={pages.ReturnedFentanylPatches}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_fentanyl_patch_log}`} element={<ProtectedRoute Component={pages.FentanylPatchLog}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_reversed_deliveries}`} element={<ProtectedRoute Component={pages.PharmacyReversedDelveries}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_tranfer_patient_concent}`} element={<ProtectedRoute Component={pages.TransferConsentsForms}></ProtectedRoute>} />
					{/* <Route exact path={`${routeNames.pharmacy_sig_check_ai}`} element={<ProtectedRoute Component={pages.SignCheckAI}></ProtectedRoute>} /> */}
					<Route exact path={`${routeNames.pharmacy_dwi}`} element={<ProtectedRoute Component={pages.PharmacyDWIPatientsData}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_investigate_rx}`} element={<ProtectedRoute Component={pages.PharmacyInvestigationRX}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_pharmanet_profile}`} element={<ProtectedRoute Component={pages.PharmacyPharmanetProfile}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_rxnumber_date_sync}`} element={<ProtectedRoute Component={pages.RxNumberDateSync}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_rx_signature_request_list}`} element={<ProtectedRoute Component={pages.PharmacyInvestigationRXSignatureRequested}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_patient_soap_notes}`} element={<ProtectedRoute Component={pages.PharmacyPatientSoapNotes}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_med_review_logs}`} element={<ProtectedRoute Component={pages.MedReviewLogs}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_create_soap_notes}`} element={<ProtectedRoute Component={pages.CreateSoapNotes}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_soap_notes_logs}`} element={<ProtectedRoute Component={pages.SoapNotesLogs}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_med_review_logs_v2}`} element={<ProtectedRoute Component={pages.MedReviewLog}></ProtectedRoute>} caseSensitive />
					<Route exact path={`${routeNames.pharmacy_super_nova_verification}`} element={<ProtectedRoute Component={pages.SuperNovaWithOtpGate}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_half_baked}`} element={<ProtectedRoute Component={pages.HalfBaked}></ProtectedRoute>} />
				</>}
				</Route>
				{/* Dispense Routes */}

				{/* Blister Packaging Routes */}
				<Route element={guard('blisters')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_blister === pharmacyLoginUser?.pharmacy_blister) && <>
					<Route exact path={`${routeNames.pharmacy_patient_blister_pack}`} element={<ProtectedRoute Component={pages.AddBlisterPack}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_blister_patients}`} element={<ProtectedRoute Component={pages.BlisterPatients}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_blister_packs}`} element={<ProtectedRoute Component={pages.BlistersPackList}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_blister_daily_packs}`} element={<ProtectedRoute Component={pages.DailyPacksList}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_blister_pack_on_shelf}`} element={<ProtectedRoute Component={pages.BlisterPacksOnShelf}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_blister_sheets}`} element={<ProtectedRoute Component={pages.BlisterSheets}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_blister_sheets_report}`} element={<ProtectedRoute Component={pages.OldBlisterSheets}></ProtectedRoute>} />


				</>}
				</Route>
				{/* Blister Packaging Routes */}

				{/* Analytics Routes */}
				<Route element={guard('packaging')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_packaging_analytics === pharmacyLoginUser?.pharmacy_packaging_analytics) && <>
					<Route exact path={`${routeNames.pharmacy_packaging_analytics}`} element={<ProtectedRoute Component={pages.AnalyticsDashboard}></ProtectedRoute>} />
				</>}
				</Route>
				{/* Analytics Routes */}

				{/* Vials Packaging Routes */}
				<Route element={guard('vials')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_patient_vials_pack === pharmacyLoginUser?.pharmacy_patient_vials_pack) && <>
					<Route exact path={`${routeNames.pharmacy_patient_vials_pack}`} element={<ProtectedRoute Component={pages.VialsPatients}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_add_vials_patient}`} element={<ProtectedRoute Component={pages.AddVialsPatient}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_vials_packs}`} element={<ProtectedRoute Component={pages.VialsPackList}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_dailyvials_packs}`} element={<ProtectedRoute Component={pages.DailyVialsList}></ProtectedRoute>} />
				</>}
				</Route>
				{/* Vials Packaging Routes */}

				{/* Speciality Medication Routes */}
				<Route element={guard('speciality_medication')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_speciality_medication === pharmacyLoginUser?.pharmacy_speciality_medication) && <>
					<Route exact path={`${routeNames.pharmacy_add_medication_patients}`} element={<ProtectedRoute Component={pages.AddMedicationPatients}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_speciality_pack}`} element={<ProtectedRoute Component={pages.MedicationPatients}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_speciality_medication_packs}`} element={<ProtectedRoute Component={pages.SpecialtyMedicationPackList}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_dailymedication_packs}`} element={<ProtectedRoute Component={pages.DailySpecialityMedicationList}></ProtectedRoute>} />
				</>}
				</Route>
				{/* Speciality Medication Routes */}

				{/* ARV Routes */}
				<Route element={guard('arv')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_arv === pharmacyLoginUser?.pharmacy_arv) && <>
					<Route exact path={`${routeNames.ADD_ARV}`} element={<ProtectedRoute Component={pages.addARV}></ProtectedRoute>} />
					<Route exact path={`${routeNames.ARV_LIST}`} element={<ProtectedRoute Component={pages.ArvList}></ProtectedRoute>} />
				</>}
				</Route>
				{/* ARV Routes */}

				{/* Revenue Management Routes */}
				<Route element={guard('revenue')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_revinue_management === pharmacyLoginUser?.pharmacy_revinue_management) && <>
					<Route exact path={`${routeNames.pharmacy_daily_revenue}`} element={<ProtectedRoute Component={pages.DailyRevenue}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_cash_change}`} element={<ProtectedRoute Component={pages.CashChange}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_deposit_info}`} element={<ProtectedRoute Component={pages.DepositInformation}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_hand_cash}`} element={<ProtectedRoute Component={pages.InHandCashInfo}></ProtectedRoute>} />
				</>}
				</Route>
				{/* Revenue Management Routes */}

				{/* FDA Routes */}
				<Route element={guard('fda_form')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_fda === pharmacyLoginUser?.pharmacy_fda) && <>
					<Route exact path={`${routeNames.create_new_fda_form}`} element={<ProtectedRoute Component={pages.CreateNewFDAForm}></ProtectedRoute>} />
					<Route exact path={`${routeNames.all_generated_fda_form_list}`} element={<ProtectedRoute Component={pages.ALLGeneratedFDAformList}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_expiring_frequent_dispense}`} element={<ProtectedRoute Component={pages.ExpiringFDAList}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_expired_frequent_dispense}`} element={<ProtectedRoute Component={pages.ExpiredFDAList}></ProtectedRoute>} />
                    <Route exact path={`${routeNames.pharmacy_fda_audit}`} element={<ProtectedRoute Component={pages.FDAAudit}></ProtectedRoute>} />
                    <Route exact path={`${routeNames.pharmacy_frequent_dispensing_authorization_timeline}`} element={<ProtectedRoute Component={pages.FDATimeline}></ProtectedRoute>} />
				</>}
				</Route>
				{/* FDA Routes */}

				{/* OAT Routes */}
				{/* Both pages are the PATIENTS module's `patients_route`
				    sub-module and are listed under the Patients menu, but they
				    sat outside every guard — nav-gated only, so typing the URL
				    still rendered them for a user without the module. The
				    legacy pharmacy_oat condition is kept verbatim inside, so a
				    legacy-mode user is unaffected. */}
				<Route element={guard('patients')}>
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_oat === pharmacyLoginUser?.pharmacy_oat) && <>
					<Route exact path={`${routeNames.pharmacy_patient_route}`} element={<ProtectedRoute Component={pages.PatientRoute}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_add_patient_route}`} element={<ProtectedRoute Component={pages.AddOatPatientPack}></ProtectedRoute>} />
				</>}
				</Route>
				{/* OAT Routes */}

				<Route exact path={`${routeNames.pharmacy_pharmacist_signature_updation}`} element={<PharmacistSignatureRoute />} />

				{/* Invoice Routes */}
				<Route element={guard('invoice')}>
				<Route exact path={`${routeNames.pharmacy_generate_invoice}`} element={<ProtectedRoute Component={pages.GenerateInvoice}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_previous_invoices}`} element={<ProtectedRoute Component={pages.PreviousInvoicesList}></ProtectedRoute>} />
				</Route>
				{/* Invoice Routes */}

				{/* Inventory Routes */}
				<Route element={guard('inventory')}>
				<Route exact path={`${routeNames.pharmacy_inventory_list}`} element={<ProtectedRoute Component={pages.InventoryList}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_inventory_accountability}`} element={<ProtectedRoute Component={pages.InventoryAccountability}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_fridge_temperature_log}`} element={<ProtectedRoute Component={pages.FridgeTemperatureLog}></ProtectedRoute>} />
				{/* <Route exact path={`${routeNames.pharmacy_total_reversed}`} element={<ProtectedRoute Component={pages.TotalReversedList}></ProtectedRoute>} /> */}
				</Route>
				{/* Reminder notifications belong to the dashboard, not the Inventory module — never module-gated. */}
				<Route exact path={`${routeNames.pharmacy_reminder_notes}`} element={<ProtectedRoute Component={pages.ReminderNotifications}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_delivered_rx_reasons}`} element={<ProtectedRoute Component={pages.DeliveredRxReasons}></ProtectedRoute>} />
				{/* Inventory Routes */}

				{/* Travel Routes */}
				<Route element={guard('travel')}>
				<Route exact path={`${routeNames.pharmacy_add_travel_patient}`} element={<ProtectedRoute Component={pages.AddTravelPatients}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_add_travel_pack}`} element={<ProtectedRoute Component={pages.TravelPatients}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_travel_logs}`} element={<ProtectedRoute Component={pages.TravelLogs}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_billing_due}`} element={<ProtectedRoute Component={pages.BillingDueList}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_travel_returning_patients}`} element={<ProtectedRoute Component={pages.ReturningPatientsList}></ProtectedRoute>} />
				</Route>
				{/* Travel Routes */}

				{/* Admin Routes */}
				{/* {adminModule !== 1 && <>
					<Route exact path={`${routeNames.pharmacy_tasks_list}`} element={<ProtectedRoute Component={pages.PharmacyEmployeesTaskList}></ProtectedRoute>} />
				</>} */}


				{/* APp Routes */}
				<Route element={guard('app')}>
				{featureFlags.appMode && <>
					<Route exact path={`${routeNames.pharmacy_app_prescriptions}`} element={<ProtectedRoute Component={pages.AppPrescriptions}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_order_transfer}`} element={<ProtectedRoute Component={pages.AppPrescriptions}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_order_new}`} element={<ProtectedRoute Component={pages.AppPrescriptions}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_view_order_details}`} element={<ProtectedRoute Component={pages.ViewOrderDetails}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_app_user_list}`} element={<ProtectedRoute Component={pages.AppUser}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_app_announcements}`} element={<ProtectedRoute Component={pages.AppAnnouncements}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_app_notification_list}`} element={<ProtectedRoute Component={pages.AppCustomNotification}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_app_refill_request}`} element={<ProtectedRoute Component={pages.AppPrescriptions}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_app_patient_chat}`} element={<ProtectedRoute Component={pages.PatientChat}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_app_insurance_documents}`} element={<ProtectedRoute Component={pages.InsuranceDocuments}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_app_pharmacy_services}`} element={<ProtectedRoute Component={pages.PharmacyAppServices}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_app_pharmacy_services_alias}`} element={<ProtectedRoute Component={pages.PharmacyAppServices}></ProtectedRoute>} />
				</>}
				</Route>
				{/* APp Routes */}


				{/* Admin Routes  */}
				{/* The Team pages: adminModule === 1 (unchanged) AND the team
				    sub-module. Settings and STAT Holidays stay out of the team
				    gate below — they are the Settings area, not Team. */}
				<Route element={guard('team')}>
				{adminModule === 1 && <>
					<Route exact path={`${routeNames.pharmacy_timeoff}`} element={<ProtectedRoute Component={adminpage.TimeOffRequest}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_check_out_in}`} element={<ProtectedRoute Component={adminpage.CheckInOutLogs}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_create_certificate}`} element={<ProtectedRoute Component={adminpage.Certificate}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_edit_certificate}`} element={<ProtectedRoute Component={adminpage.Certificate}></ProtectedRoute>} />
					{/* <Route exact path={`${routeNames.pharmacy_create_new_task}`} element={<ProtectedRoute Component={adminpage.CreateTaskManger}></ProtectedRoute>} /> */}
					<Route exact path={`${routeNames.pharmacy_team_attendance}`} element={<ProtectedRoute Component={adminpage.TeamTimeSheet}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_certificate_template}`} element={<ProtectedRoute Component={adminpage.ViewCertificateTemplates}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_assign_certificate}`} element={<ProtectedRoute Component={adminpage.EmployeCertificateTemplates}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_assign_certificate_employee}`} element={<ProtectedRoute Component={adminpage.AssignCertificateTemplate}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_user}`} element={<ProtectedRoute Component={adminpage.ManageTeamList}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_department}`} element={<ProtectedRoute Component={adminpage.AssignDepartments}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_team_avalibility}`} element={<ProtectedRoute Component={adminpage.TeamSchedule}></ProtectedRoute>} />
					{/* <Route exact path={`${routeNames.pharmacy_driver}`} element={<ProtectedRoute Component={pages.DeliveryDeriverManagement}></ProtectedRoute>} /> */}
				</>}
				</Route>
				{adminModule === 1 && <>
					<Route exact path={`${routeNames.pharmacy_settings}`} element={<ProtectedRoute Component={adminpage.ManagePharmacySettings}></ProtectedRoute>} />
					<Route exact path={`${routeNames.pharmacy_holidays_calander}`} element={<ProtectedRoute Component={pages.PharmacyStatHolidays}></ProtectedRoute>} />
				</>}
				{/* Admin Routes  */}

				{/* Task Management Routes */}
				{/* Both are mapped to the TEAM module in moduleSubItems, and the
				    nav hides them without it — but the routes themselves were
				    unguarded, so the URL still worked. */}
				<Route element={guard('team')}>
				<Route exact path={`${routeNames.pharmacy_task_management}`} element={<ProtectedRoute Component={adminpage.TaskManagement}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_tasks}`} element={<ProtectedRoute Component={adminpage.AllTasksList}></ProtectedRoute>} />
				</Route>
				{/* Task Management Routes */}

				{/* Appointmets Route */}
				{/* These two were the only sub-module-backed pages left with no
				    gate at all: `appointments_book` / `appointments_logs` are
				    seeded in the registry, but the routes sat outside every
				    guard and were unmapped in moduleSubItems. The backfill puts
				    `appointments` in the ALWAYS_ON set, so every existing
				    pharmacy/department holds both children and keeps today's
				    access; legacy mode (modules === null) is unchanged. */}
				<Route element={guard('appointments')}>
				<Route exact path={`${routeNames.pharmacy_juno_appointment}`} element={<ProtectedRoute Component={pages.BookAppointments}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_appointment_logs}`} element={<ProtectedRoute Component={pages.AppointmentLogs}></ProtectedRoute>} />
				</Route>
				{/* Appointmets Route */}

				{/* Packaging Route */}
				<Route element={guard('packaging')}>
				<Route exact path={`${routeNames.pharmacy_packaging_order_preview}`} element={<ProtectedRoute Component={pages.PackagingOrderPreview}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_packaging_processing}`} element={<ProtectedRoute Component={pages.CreateNewOrder}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_packaging_orders}`} element={<ProtectedRoute Component={pages.AllPackagingOrders}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_packaging_monthly_invoice}`} element={<ProtectedRoute Component={pages.PackagingMonthlyInvoice}></ProtectedRoute>} />

				{/* Registered for everyone: the backend returns only the caller's own
				    files, so a self-fulfil pharmacy gets its files here and anyone
				    else gets an empty list — gating on the hub id 404'd self-fulfil. */}
				<Route exact path={`${routeNames.pharmacy_packaging_all_d_logs}`} element={<ProtectedRoute Component={pages.MyPackmedFiles}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_packaging_din_management}`} element={<ProtectedRoute Component={pages.PacmedDinManagement}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_packaging_liquid_processing}`} element={<ProtectedRoute Component={pages.PackgingLiquidProcessing}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_packaging_prn_processing}`} element={<ProtectedRoute Component={pages.PackgingPRNProcessing}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_packaging_invoices}`} element={<ProtectedRoute Component={pages.AllInvoices}></ProtectedRoute>} />
				{/* </>} */}
				</Route>
				{/* Packaging Route */}

				{/* Vaccination Route */}
				<Route element={guard('vaccine')}>
				<Route exact path={`${routeNames.pharmacy_vaccine_passport}`} element={<ProtectedRoute Component={pages.VaccinePassPort}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_vaccine_logs}`} element={<ProtectedRoute Component={pages.VaccineLogs}></ProtectedRoute>} />
				</Route>
				{/* Vaccination Route */}

				{/* SMS Route */}
				{/* {process.env.NODE_ENV === 'development' && <> */}
				<Route exact path={`${routeNames.pharmacy_sms_tabs}`} element={<ProtectedRoute Component={pages.PharmacySmsTabs}></ProtectedRoute>} />
				{/* </>} */}
				{/* SMS Route */}

				{/* Blister Packaging Route */}
				<Route element={guard('packaging')}>
				<Route exact path={`${routeNames.pharmacy_blister_packaging_processing}`} element={<ProtectedRoute Component={pages.CreateBlisterPackagingNewOrder}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_blister_packaging_prn_processing}`} element={<ProtectedRoute Component={pages.BlisterPackgingPRNProcessing}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_blister_packaging_liquid_processing}`} element={<ProtectedRoute Component={pages.BlisterPackgingLiquidProcessing}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_blister_packaging_orders}`} element={<ProtectedRoute Component={pages.BlisterPackagingAllOrders}></ProtectedRoute>} />
				{/* Registered for everyone — same reasoning as the PacMed files route. */}
				<Route exact path={`${routeNames.pharmacy_blister_packaging_packmed_files}`} element={<ProtectedRoute Component={pages.BlisterPackagingPackmedFiles}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_blister_packaging_invoices}`} element={<ProtectedRoute Component={pages.BlisterPackagingAllInvoices}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_blister_packaging_monthly_invoice}`} element={<ProtectedRoute Component={pages.BlisterPackagingMonthlyInvoice}></ProtectedRoute>} />
				</Route>

				{/* Blister Packaging Route */}

				{/* Account Ability Route */}

				<Route element={guard('accountability')}>
				<Route exact path={`${routeNames.pharmacy_accountability_new_rx}`} element={<ProtectedRoute Component={pages.AccountabilityNewRx}></ProtectedRoute>} />
				{/* {!showAfterProcessPrepCheck && ( */}
					<Route exact path={`${routeNames.pharmacy_accountability_prep_check}`} element={<ProtectedRoute Component={pages.PrepCheck}></ProtectedRoute>} />
				{/* )} */}
				<Route exact path={`${routeNames.pharmacy_accountability_prep_for_new_rx}`} element={<ProtectedRoute Component={pages.PrepForNewRx}></ProtectedRoute>} />

				{profileData?.type === 'pharmacist' && accountabilitySettings === 'preparation' && <>
					<Route exact path={`${routeNames.pharmacy_accountability_final_check}`} element={<ProtectedRoute Component={pages.FinalCheck}></ProtectedRoute>} />
				</>}


				<Route exact path={`${routeNames.pharmacy_accountability_today_missed_rx}`} element={<ProtectedRoute Component={pages.TodayMissedRx}></ProtectedRoute>} />
				{/* {showAfterProcessPrepCheck && ( */}
					<Route exact path={`${routeNames.pharmacy_accountability_after_process_prep_check}`} element={<ProtectedRoute Component={pages.AfterProcessPrepCheck}></ProtectedRoute>} />
				{/* )} */}
				<Route exact path={`${routeNames.pharmacy_mark_flexi}`} element={<ProtectedRoute Component={pages.PharmacyMarkFlexi}></ProtectedRoute>} />
				<Route exact path={`${routeNames.pharmacy_accountability_all_reports}`} element={<ProtectedRoute Component={pages.AccountabilityAllReports}></ProtectedRoute>} />

				{/* Both of these key off an ACCOUNTABILITY sub-module
				    (accountability_new_rx / accountability_pharmacare), so they
				    belong under guard('accountability'). They used to sit under
				    guard('dispense') and guard('forms') respectively, where the
				    parent check and the sub-module check tested different
				    modules — losing `dispense`/`forms` hid a page the user still
				    held, and holding them let a user reach a page whose own
				    sub-module they did not have.
				    Their LEGACY permission conditions are carried over verbatim,
				    so nothing changes for a legacy-mode user. */}
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_daily_dispense === pharmacyLoginUser?.pharmacy_daily_dispense) &&
					<Route exact path={`${routeNames.pharmacy_accountability}`} element={<ProtectedRoute Component={pages.Accountability}></ProtectedRoute>} />
				}
				{shouldRegisterModuleRoutes(isPermissionAccess?.pharmacy_forms === pharmacyLoginUser?.pharmacy_forms) &&
					<Route exact path={`${routeNames.pharmacy_pharmacare}`} element={
						<Suspense fallback={<RouteLoadingFallback />}>
							<ProtectedRoute Component={Pharmacare} />
						</Suspense>
					} />
				}
				</Route>

				{/* Reached from a phone via a shared link — deliberately outside the accountability module gate. */}
				<Route exact path={`${routeNames.pharmacy_accountability_upload_pics}`} element={<ProtectedRoute mobileOnly Component={pages.UploadPics}></ProtectedRoute>} />

				{/* <Route exact path={`${routeNames.pharmacy_accountability_pickup_log}`} element={<ProtectedRoute Component={pages.PickupLog}></ProtectedRoute>} />					 */}


				{/* eRx is an ACCOUNTABILITY dropdown item (mapped to the
				    `accountability` module in moduleSubItems) and the nav hides
				    it without that module — but the route was outside the
				    guard, so the URL still rendered it. The pharmacyId === 1502
				    condition is kept verbatim. */}
				<Route element={guard('accountability')}>
				{pharmacyId === 1502 && <>
					<Route exact path={`${routeNames.pharmacy_erx}`} element={<ProtectedRoute Component={pages.ERx}></ProtectedRoute>} />
				</>}
				</Route>

				{token && location.pathname === routeNames.HOME_ROUTE ?
					<Route exact path="*" element={<ProtectedRoute Component={pages.Dashboard}></ProtectedRoute>} />
					: token ?
						<Route path="*" element={<NotFound />} /> :
						<Route path="*" element={<SignIn />} />}

			</Routes>

		</>
	)
}

export default AppRoutes
